// Client mirror of functions-common/src/charts/datasets/stock.ts. Keep in sync.

import type { DatasetDefinition } from '../types.js'

export const stockDataset: DatasetDefinition = {
  key: 'stock',
  labelKey: 'NLSChartDatasetStock',
  fields: [
    { name: 'item', labelKey: 'NLSChartFieldItem', kind: 'dimension', type: 'string' },
    { name: 'category', labelKey: 'NLSChartFieldCategory', kind: 'dimension', type: 'string' },
    { name: 'supplier', labelKey: 'NLSChartFieldSupplier', kind: 'dimension', type: 'string' },
    { name: 'location', labelKey: 'NLSChartFieldLocation', kind: 'dimension', type: 'string' },
    { name: 'lowStock', labelKey: 'NLSChartFieldLowStock', kind: 'dimension', type: 'boolean' },
    { name: 'onHand', labelKey: 'NLSChartFieldOnHand', kind: 'measure', type: 'number' },
    { name: 'value', labelKey: 'NLSChartFieldValue', kind: 'measure', type: 'number' },
    { name: 'lowStockCount', labelKey: 'NLSChartFieldLowStockCount', kind: 'measure', type: 'number' },
    { name: 'countsSubmitted', labelKey: 'NLSChartFieldCountsSubmitted', kind: 'measure', type: 'number' },
    { name: 'countDate', labelKey: 'NLSChartFieldCountDate', kind: 'time', type: 'date' },
    { name: 'movementDate', labelKey: 'NLSChartFieldMovementDate', kind: 'time', type: 'date' },
  ],
  defaultMeasure: { field: 'onHand', agg: 'sum' },
  defaultTimeField: 'countDate',
  allowedRoles: ['OWNER', 'ADMIN', 'EDITOR', 'VIEWER', 'HELPER'],
  available: true,
}

function toDateSafe(val: unknown): Date | null {
  if (!val) return null
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val
  if (typeof (val as { toDate?: () => Date }).toDate === 'function') {
    try {
      const d = (val as { toDate: () => Date }).toDate()
      return isNaN(d.getTime()) ? null : d
    } catch {
      return null
    }
  }
  if (typeof (val as { toMillis?: () => number }).toMillis === 'function') {
    try {
      const ms = (val as { toMillis: () => number }).toMillis()
      const d = new Date(ms)
      return isNaN(d.getTime()) ? null : d
    } catch {
      return null
    }
  }
  if (typeof val === 'number') {
    const d = new Date(val)
    return isNaN(d.getTime()) ? null : d
  }
  if (typeof val === 'string') {
    const d = new Date(val)
    return isNaN(d.getTime()) ? null : d
  }
  return null
}

export function buildStockRow(
  data: Record<string, unknown>,
  id: string,
): Record<string, unknown> {
  const countDate = toDateSafe(data.lastCountDate) ?? toDateSafe(data.countDate)
  const movementDate = toDateSafe(data.movementDate)

  const onHand = typeof data.quantity === 'number'
    ? data.quantity
    : (typeof data.quantityOnHand === 'number'
      ? data.quantityOnHand
      : (typeof data.onHand === 'number' ? data.onHand : 0))

  const unitCost = typeof data.unitCost === 'number' ? data.unitCost : 0
  const value = typeof data.value === 'number' ? data.value : (onHand * unitCost)

  const threshold = typeof data.reorderPoint === 'number'
    ? data.reorderPoint
    : (typeof data.minThreshold === 'number'
      ? data.minThreshold
      : (typeof data.lowStockThreshold === 'number' ? data.lowStockThreshold : null))

  const isLowStock = threshold !== null && onHand <= threshold
  const lowStockCount = isLowStock ? 1 : 0

  return {
    id,
    _id: id,
    item: typeof data.name === 'string' ? data.name : (typeof data.title === 'string' ? data.title : id),
    category: typeof data.category === 'string' ? data.category : '',
    supplier: typeof data.supplierId === 'string' ? data.supplierId : (typeof data.supplier === 'string' ? data.supplier : ''),
    location: typeof data.locationId === 'string' ? data.locationId : (typeof data.location === 'string' ? data.location : ''),
    lowStock: isLowStock,
    onHand,
    value,
    lowStockCount,
    countsSubmitted: typeof data.countsSubmitted === 'number' ? data.countsSubmitted : 1,
    countDate: countDate ? countDate.toISOString() : null,
    movementDate: movementDate ? movementDate.toISOString() : null,
    title: typeof data.name === 'string' ? data.name : (typeof data.title === 'string' ? data.title : ''),
  }
}
